/**
 * Итоговые характеристики боя: базовые данные баланса + уровень/звёзды героя + модификаторы ({@link StatModifier}).
 *
 * Модификаторы собирает {@link collectRunModifiers} из забежки: уровни Tech и взятые усиления (уровни и стаки
 * одного источника складываются). Выбор усилений и цены — E4/E5; здесь только применение.
 * Порядок по контракту: все `add`, затем произведение всех `mulPermille` (по очереди, с отбрасыванием дроби).
 *
 * `projectileCount`, `skillCooldown`, `skillPower` применяют герои (E3, `engine/src/heroes/skill-stats.ts`).
 */
import type { AttackDef, BalanceData, HeroId, RunState, StatId, StatModifier } from "@newfungame/contracts";
import { PERMILLE } from "@newfungame/contracts";
import { foldLevels } from "../economy/modifiers";
import { divTrunc, mulPermille } from "./math";

/** Базовый множитель крита, промилле (×1,5), если модификаторы не дают другого. */
export const BASE_CRIT_DAMAGE = 1500;

/** Кто получает модификатор: герой или штаб. */
export type StatOwner = HeroId | "hq";

/** Атака после всех модификаторов. */
export interface ResolvedAttack extends AttackDef {
  /** Шанс крита, промилле. */
  critChance: number;
  /** Множитель урона крита, промилле. */
  critDamage: number;
}

function matches(mod: StatModifier, who: StatOwner): boolean {
  const t = mod.target ?? "allHeroes";
  return t === who || (t === "allHeroes" && who !== "hq");
}

/**
 * Применить к `base` все модификаторы характеристики `stat`. `who = null` — характеристика общая
 * (монеты, скорость зомби, штаб): поле `target` модификатора не проверяется.
 */
export function applyStat(base: number, stat: StatId, who: StatOwner | null, mods: readonly StatModifier[]): number {
  let v = base;
  for (const m of mods) if (m.stat === stat && m.op === "add" && (who === null || matches(m, who))) v += m.value;
  for (const m of mods) {
    if (m.stat === stat && m.op === "mulPermille" && (who === null || matches(m, who))) {
      v = mulPermille(v, PERMILLE + m.value);
    }
  }
  return v;
}

/**
 * Модификаторы забежки: Tech × уровень и усиления × стаки (в порядке данных баланса).
 * Уровни и стаки одного источника складываются (правило E5, `economy.foldLevels`): 5 стаков «+10 %» — один
 * модификатор +50 %, а не 1,1⁵; разные источники перемножаются в {@link applyStat}.
 */
export function collectRunModifiers(balance: BalanceData, run: RunState): StatModifier[] {
  const out: StatModifier[] = [];
  for (const t of balance.tech) out.push(...foldLevels(t.modifiers, run.tech[t.id] ?? 0));
  for (const b of run.boosts) {
    const def = balance.boosts.find((d) => d.id === b.boostId);
    if (def) out.push(...foldLevels(def.modifiers, b.stacks));
  }
  return out;
}

function resolveAttack(
  base: AttackDef,
  damage: number,
  who: StatOwner,
  mods: readonly StatModifier[],
  extra?: { damage: StatId; fireRate: StatId },
): ResolvedAttack {
  let dmg = applyStat(damage, "damage", who, mods);
  let rate = applyStat(PERMILLE, "fireRate", who, mods);
  if (extra) {
    dmg = applyStat(dmg, extra.damage, null, mods);
    rate = applyStat(rate, extra.fireRate, null, mods);
  }
  return {
    ...base,
    damage: Math.max(1, dmg),
    cooldownTicks: Math.max(1, divTrunc(base.cooldownTicks * PERMILLE, Math.max(1, rate))),
    range: Math.max(1, applyStat(base.range, "range", who, mods)),
    critChance: Math.min(PERMILLE, Math.max(0, applyStat(base.baseCritChance ?? 0, "critChance", who, mods))),
    critDamage: Math.max(PERMILLE, applyStat(base.baseCritDamage ?? BASE_CRIT_DAMAGE, "critDamage", who, mods)),
  };
}

/** Атака героя: урон растёт с уровнем (`damagePerLevelPermille`) и звёздами (`economy.stars`), затем модификаторы. */
export function heroAttack(
  balance: BalanceData,
  heroId: HeroId,
  level: number,
  stars: number,
  mods: readonly StatModifier[],
): ResolvedAttack | null {
  const def = balance.heroes.find((h) => h.id === heroId);
  if (!def) return null;
  const growth =
    PERMILLE + def.damagePerLevelPermille * (level - 1) + balance.economy.stars.damagePerStarPermille * stars;
  return resolveAttack(def.attack, mulPermille(def.attack.damage, growth), heroId, mods);
}

/** Штаб и пушка после модификаторов. */
export interface HqStats {
  hpMax: number;
  regenPerTick: number;
  cannon: ResolvedAttack;
}

export function hqStats(balance: BalanceData, mods: readonly StatModifier[]): HqStats {
  const c = balance.hq.cannon;
  return {
    hpMax: Math.max(1, applyStat(balance.hq.hp, "hqMaxHp", null, mods)),
    regenPerTick: Math.max(0, applyStat(balance.hq.regenPerTick, "hqRegen", null, mods)),
    cannon: resolveAttack(c, c.damage, "hq", mods, { damage: "cannonDamage", fireRate: "cannonFireRate" }),
  };
}

/** Множители, общие для всего боя, промилле. */
export function globalStats(mods: readonly StatModifier[]): { coinGain: number; zombieSpeed: number } {
  return {
    coinGain: Math.max(0, applyStat(PERMILLE, "coinGain", null, mods)),
    zombieSpeed: Math.max(0, applyStat(PERMILLE, "zombieSpeed", null, mods)),
  };
}
